import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import {Box, Stack, Typography} from "@mui/material";
import {useMemo, useState} from "react";
import {removeTeamMember, resendTeamInvitation, resolveCustomerTeamMember} from "@/services/customer.ts";

import {green, grey, yellow} from "@mui/material/colors";

import dayjs from "dayjs";
import {DataGridSimpleStyled, GridColDef} from "@/components/Shared/DataGridSimple";

import {useConfirmationContext} from "@/contexts/ConfirmationContext";
import {useNotificationContext} from "@/contexts/NotificationContext";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import {NavLink} from "react-router-dom";
import Utils from "@/components/Shared/Utils";
import RefetchButton from "@/components/Shared/Utils/RefetchButton";

export default function TeamMembers({customer, team, close}: any) {
    const {setNotification} = useNotificationContext()
    const {setConfirmation} = useConfirmationContext()

    const {data, refetch} = resolveCustomerTeamMember(team);

    const [filter, setFilter] = useState(null);

    const teammates = useMemo(() => filter ? data?.teammates?.filter((member: any) => member.email?.toLowerCase()?.match(filter)) : data?.teammates, [data, filter])

    async function handleResendTeamInvitation(member: any) {
        const data = await resendTeamInvitation(team?.owner, member.id)

        if (data) {
            setNotification({
                message: `We've just sent ${member.email} an invitation to join your team! They should receive it soon.`,
                open: true
            })
        }

        await refetch()
    }

    async function handleRemoveTeamMember(member: any) {
        const data = await removeTeamMember(team.owner, member.id)

        if (data) {
            setNotification({
                message: `We've removed ${member.email} from the team. This means they can't access team resources anymore.`,
                open: true
            })
        }

        close()
        refetch()
    }

    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'ID',
            width: 100,
            editable: false,
            filterable: false
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 250,
            editable: false,
            filterable: false
        },
        {
            field: 'active',
            headerName: 'Active',
            width: 100,
            editable: false,
            filterable: false,
            renderCell: ({row}) => row.active && <Typography fontSize={13} color={green[700]}>Active</Typography> || '-'
        },
        {
            field: 'pending',
            headerName: 'Invite Pending',
            width: 150,
            editable: false,
            filterable: false,
            renderCell: ({row}) => row.pending &&
                <TimelapseOutlinedIcon sx={{fontSize: 20, color: yellow[800]}}/> || '-'
        },
        {
            field: 'invited_at',
            headerName: 'Invited At',
            width: 175,
            editable: false,
            filterable: false,
            renderCell: ({value}) => <Typography
                fontSize={13}>{dayjs(value)?.format("MMMM DD, YYYY, hh:mm:ss")}</Typography>
        },
        {
            field: 'joined_at',
            headerName: 'Joined At',
            width: 175,
            editable: false,
            filterable: false,
            renderCell: ({value}) => value &&
                <Typography fontSize={13}>{dayjs(value)?.format("MMMM DD, YYYY, hh:mm:ss")}</Typography> || '-'
        },
        {
            field: 'action',
            headerName: 'Actions',
            width: 250,
            editable: false,
            filterable: false,
            sortable: false,
            renderCell: ({row}) => (
                <>
                    {row.pending && (
                        <Typography
                            fontSize={13}
                            sx={{mr: 1, color: grey[600], cursor: 'pointer', "&:hover": {color: grey[900]}}}
                            onClick={() => setConfirmation({
                                dialog: {
                                    title: `Resend Invitation`,
                                    acceptMessage: 'Send',
                                    description: `This will send a fresh copy of the original invitation directly to email ${row.email}.`,
                                },
                                show: true,
                                callback: () => handleResendTeamInvitation(row)
                            })}
                        >
                            Resend Invitation
                        </Typography>
                    )}
                    <Typography
                        fontSize={13}
                        sx={{mr: 1, color: grey[600], cursor: 'pointer', "&:hover": {color: grey[900]}}}
                        onClick={() => setConfirmation({
                            dialog: {
                                title: `Remove Teammate`,
                                acceptMessage: 'Remove',
                                description: `This action will permanently remove ${row.email} from the team and its resources. Proceed with caution.`,
                            },
                            show: true,
                            callback: () => handleRemoveTeamMember(row)
                        })}
                    >
                        Remove
                    </Typography>
                </>

            )
        }
    ]

    return (
        <>
            <Stack direction="row" justifyContent="space-between">
                <Box width="75%">
                    <Box mb={2}>
                        <Utils.ObjectFilter callback={setFilter} label="Find teammates by email"/>
                    </Box>
                </Box>
                <Stack width="25%" direction="row" justifyContent="end">
                    <Utils.FormUtils.PrimaryButton disabled={!customer} light component={NavLink} sx={{mr: 1}} startIcon={<AddOutlinedIcon/>}
                                                   to="/account/organization/teams/invitation">
                        Invite Teammate
                    </Utils.FormUtils.PrimaryButton>
                    <RefetchButton refetch={refetch}/>
                </Stack>
            </Stack>

            <DataGridSimpleStyled rows={teammates} columns={columns} hideFooter/>
        </>
    )
}